/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module UpdateModule
 */

import {
    Component,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';

import {
    IUpgradePreviewResponse,
    IUpgradeResponse,
    PatchType,
    RollbackType,
    SeGroupErrorRecovery,
    UpgradeFsmState,
    UpgradeOperation,
} from 'generated-types';

import { ClrLoadingState } from '@cds/core/button';
import { ClrWizard, ClrWizardPage } from '@clr/angular';
import { createDropdownOption } from 'ng/shared/utils/dropdown.utils';
import { IAviDropdownOption } from 'standalone-components/avi-dropdown/avi-dropdown.types';
import { IEnumValue } from 'ajs/modules/core/services/schema-service/schema.types';
import { IHttpResponse } from 'angular';
import { Image } from 'ajs/modules/upgrade/factories/image.item.factory';
import { ImageCollection, TImageCollection } from 'ajs/modules/upgrade/factories/image-collection.factory';
import { ITSEGroup } from 'ajs/modules/service-engine-group/factories/se-group.item.factory';
import { L10nService } from '@vmw/ngx-vip';
import { SchemaService } from 'ajs/modules/core/services/schema-service/schema.service';
import { SeGroupOptions, UpgradeParams } from 'object-types';
import { StateService } from '@uirouter/core';
import { UpdateService } from 'ng/modules/update/services/update.service';
import * as globalL10n from 'global-l10n';
import * as StateNames from 'state-names';

import {
    IImageSelectionData,
    imageCategoryHash,
    imageTypeHash,
    IPrecheckProgressConfig,
    IUpdateConfig,
    UpdateWizardStage,
    UpgradeType,
} from '../../update.types';

import './update-wizard.component.less';
import { UpdateStore } from '../update.store';
import * as l10n from './update-wizard.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * Set of upgrade types with suppress warning flow.
 */
const UPGRADE_TYPES_WITH_WARNING_FLOW = new Set([
    UpgradeType.UPGRADE_TYPE_SEG_UPGRADE,
    UpgradeType.UPGRADE_TYPE_SYSTEM_UPGRADE,
    UpgradeType.UPGRADE_TYPE_SYSTEM_RESTORE,
    UpgradeType.UPGRADE_TYPE_SYSTEM_ROLLBACK,

]);

/**
 * @description Update wizard component.
 *
 * @author Dhanashree Tilak, Deepak Chaudhary
 */
@Component({
    selector: 'update-wizard',
    templateUrl: './update-wizard.component.html',
})
export class UpdateWizardComponent implements OnChanges {
    /**
     * TemplateRef for wizard.
     */
    @ViewChild('wizard')
    public readonly wizard: ClrWizard;

    /**
     * TemplateRef for let's begin page.
     */
    @ViewChild('selectSegUpdatePage')
    public readonly selectSegUpdatePage: ClrWizardPage;

    /**
     * TemplateRef for let's begin page.
     */
    @ViewChild('letsBeginPage')
    public readonly letsBeginPage: ClrWizardPage;

    /**
     * TemplateRef for pre-check page.
     */
    @ViewChild('preCheckPage')
    public readonly preCheckPage: ClrWizardPage;

    /**
     * Update configuration.
     */
    @Input()
    public upgradeConfig?: IUpdateConfig;

    /**
     * Selected System Report Id.
     */
    @Input()
    public systemReportId: string;

    /**
     * Wizard state
     */
    @Input()
    public isWizardOpen = false;

    @Input()
    public currentUpdateWizardStage?: UpdateWizardStage;

    /**
     * Track whether the wizard is opened from the System Update page or the SEG page.
     */
    @Input()
    public isSystemUpdate = false;

    /**
     * Track whether the wizard is opened for Restore.
     */
    @Input()
    public isRestore = false;

    /**
     * Track whether the wizard is opened for Rollback.
     */
    @Input()
    public isRollback = false;

    /**
     * Store node pre-check status.
     */
    @Input()
    public preCheckProgressConfig?: IPrecheckProgressConfig;

    /**
     * Store ids of selected SEG node for Update operations.
     */
    @Input()
    public segUpgradeNodeList: ITSEGroup[] = [];

    /**
     * Fire on click of cancel button on the dialog.
     */
    @Output()
    public onClose = new EventEmitter<void>();

    /**
     * Fire on update operation.
     */
    @Output()
    public onUpdate = new EventEmitter<boolean>();

    /**
     * Trigger an event upon completion of rollback/upgrade to notify the parent component
     * whether SEG upgrade option is selected.
     */
    @Output()
    public onSelectSegUpdate = new EventEmitter<boolean>();

    /**
     * Translation keys for the wizard component.
     */
    public readonly l10nKeys = l10nKeys;

    /**
     * Global translation keys.
     */
    public readonly globalL10nKeys = globalL10nKeys;

    /**
     * Title of the wizard component.
     */
    public wizardTitle: string;

    /**
     * Expose UpgradeType enum values for use within the template.
     */
    public UpgradeType = UpgradeType;

    /**
     * Hash of ClrLoadingState enum for clr button state.
     */
    public loadingStateEnumHash = {
        default: ClrLoadingState.default,
        loading: ClrLoadingState.loading,
    };

    /**
     * ObjectTypes used in template.
     */
    public readonly objectTypes = {
        SeGroupOptions,
        UpgradeParams,
    };

    /**
     * True when applying upgrade/rollback to controller and all service engine groups.
     * False when only upgrading/rollback controller.
     */
    public applyToSystem = false;

    /**
     * ID of the system image selected when opening the wizard, if there exists one.
     */
    public initialSystemImageId = '';

    /**
     * Patch image ID which will be applied to the SEGs for system upgrade.
     */
    public selectedSegSystemUpgradePatchImageId = '';

    /**
     * List of options of compatible SEG system upgrade patch versions.
     */
    public segSystemUpgradePatchVersionOptions: IAviDropdownOption[] = [];

    /**
     * List values of segErrorRecovery enum.
     */
    public segErrorRecoveryEnumValues: IEnumValue[];

    /**
     * Hash of SeGroupErrorRecovery enum.
     */
    public readonly seGroupErrorRecoveryEnumHash = {
        ROLLBACK_UPGRADE_OPS_ON_ERROR: SeGroupErrorRecovery.ROLLBACK_UPGRADE_OPS_ON_ERROR,
        CONTINUE_UPGRADE_OPS_ON_ERROR: SeGroupErrorRecovery.CONTINUE_UPGRADE_OPS_ON_ERROR,
        SUSPEND_UPGRADE_OPS_ON_ERROR: SeGroupErrorRecovery.SUSPEND_UPGRADE_OPS_ON_ERROR,
    };

    /**
     * Check point list from backend for users to confirm before continuing with must checks.
     */
    public previewCheckList: string[];

    /**
     * True when PreUpgradePrompts returns errors.
     */
    public fetchUpgradePreviewFailed = false;

    /**
     * Busy flag to render a spinner.
     */
    public busy = false;

    /**
     * Error to be displayed if an error occurs.
     */
    public error: string | null;

    /**
     * Flag indicating if is upgrade or rollback mode.
     */
    public isUpgradeMode: boolean;

    /**
     * Show/Hide SE group upgrade related fields.
     */
    public isSegUpgradeAvailable: boolean;

    public upgradeType: UpgradeType | undefined;

    /**
     * Action to be taken when SEG upgrade hits error.
     */
    public actionOnSegFailure: SeGroupErrorRecovery;

    /**
     * Show/Hide Upgrade all SEG checkbox.
     */
    public showUpgradeSegCheckbox: boolean;

    /**
     * Checkbox to proceed upgrade operation in case of warning.
     */
    public suppressWarning = false;

    /**
     * The current state of the upgrade wizard.
     */
    public currentState: UpgradeFsmState;

    /**
     * Restore file.
     */
    public restoreFile: string;

    /**
     * Passphrase for restore file.
     */
    public passphrase: string;

    /**
     * Pre-check state hash.
     */
    public readonly upgradeFsmState = {
        ERROR: UpgradeFsmState.UPGRADE_PRE_CHECK_ERROR,
        IN_PROGRESS: UpgradeFsmState.UPGRADE_PRE_CHECK_IN_PROGRESS,
        SUCCESS: UpgradeFsmState.UPGRADE_PRE_CHECK_SUCCESS,
        WARNING: UpgradeFsmState.UPGRADE_PRE_CHECK_WARNING,
        STARTED: UpgradeFsmState.UPGRADE_PRE_CHECK_STARTED,
    };

    constructor(
        @Inject(ImageCollection)
        private readonly ImageCollectionFactory: TImageCollection,
        private readonly $state: StateService,
        private readonly l10nService: L10nService,
        private readonly schemaService: SchemaService,
        private readonly updateService: UpdateService,
        private readonly updateStore: UpdateStore,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    public ngOnChanges(changes: SimpleChanges): void {
        if (this.upgradeConfig) {
            this.upgradeType = this.upgradeConfig.upgradeType;
            this.segErrorRecoveryEnumValues =
                this.schemaService.getEnumValues('SeGroupErrorRecovery');

            this.isUpgradeMode = this.isUpgrade();
            this.wizardTitle = this.getWizardTitle();
            this.isSegUpgradeAvailable = UpdateService.isSegUpgradeAvailable(this.upgradeConfig);

            this.applyToSystem = this.isSegUpgradeAvailable && this.isSystemUpdate;

            this.showUpgradeSegCheckbox = this.isSegUpgradeAvailable &&
                (this.isType(UpgradeType.UPGRADE_TYPE_SYSTEM_UPGRADE) ||
                    this.isType(UpgradeType.UPGRADE_TYPE_SYSTEM_ROLLBACK));

            // Sets default value in `Action to take` radio button for upgrade mode.
            this.actionOnSegFailure = SeGroupErrorRecovery.SUSPEND_UPGRADE_OPS_ON_ERROR;

            if (this.isSystemUpgrade() && this.isSegUpgradeAvailable) {
                this.setInitialImageIds();
                this.initSegSystemUpgradePatchVersionOptions();
            }

            // Preview Checklist API call should be made only if the user is on Let's Begin Step.
            if (this.currentUpdateWizardStage === UpdateWizardStage.LETS_BEGIN) {
                this.populatePreviewCheckList();
            }
        }

        if (changes.currentUpdateWizardStage && !changes.currentUpdateWizardStage.isFirstChange()) {
            this.setActiveWizardStage();
        }
    }

    /**
     * Jump to the specified wizard page.
     *
     * If the page is marked as completed, jump to that page.
     * Otherwise, jump to the last enabled page.
     *
     * @param page - The page to jump to.
     */
    private setActiveWizardStage(): void {
        let currentPage: ClrWizardPage;

        switch (this.currentUpdateWizardStage) {
            case UpdateWizardStage.PRE_CHECK:
                this.letsBeginPage.completed = true;

                if (!this.isSystemUpdate) {
                    this.selectSegUpdatePage.completed = true;
                }

                currentPage = this.preCheckPage;
                break;
            case UpdateWizardStage.SEG_COMPATIBLE_UPDATES:
                currentPage = this.selectSegUpdatePage;
                break;
            case UpdateWizardStage.LETS_BEGIN:
            default:
                currentPage = this.letsBeginPage;
                break;
        }

        this.handleJumpToPage(currentPage);
    }

    /**
     * Jump to the specified page in the wizard.
     *
     * If the page is marked as completed, jump to that page.
     * Otherwise, jump to the last enabled page.
     *
     * @param page - The page to jump to.
     */
    private handleJumpToPage(page: ClrWizardPage): void {
        if (page) {
            this.wizard.navService.currentPage = page;
        } else {
            this.wizard.navService.setLastEnabledPageCurrent();
        }
    }

    /**
     * Returns true if the "Let's Begin" page should be disabled.
     * The "Let's Begin" page should be disabled if:
     * - The current wizard stage is the "Let's Begin" stage, AND
     * - Any of the following:
     *   - The wizard is busy (e.g., preview API call in progress)
     *   - The wizard is in restore mode and no restore file has been selected
     *   - The upgrade preview API call failed
     */
    public get isLetsBeginPageDisabled(): boolean {
        return this.currentUpdateWizardStage === UpdateWizardStage.LETS_BEGIN &&
            (this.busy || this.isRestore && !this.restoreFile || this.fetchUpgradePreviewFailed);
    }

    /**
     * Handle submit.
     */
    public handleSubmit(): void {
        const {
            actionOnSegFailure,
            applyToSystem,
            passphrase,
            restoreFile,
            updateStore,
            upgradeConfig,
            upgradeType,
        } = this;

        if (upgradeConfig) {
            upgradeConfig.upgradeType = upgradeType;
        }

        const userSelectedOptions = {
            actionOnSegFailure,
            applyToSystem,
            passphrase,
            file: restoreFile,
            upgradeConfig,
            upgradeType,
            isRollback: this.isRollback,
        };

        this.error = null;
        this.busy = true;

        switch (this.upgradeType) {
            case UpgradeType.UPGRADE_TYPE_SYSTEM_PRECHECK:
            case UpgradeType.UPGRADE_TYPE_SEG_PRECHECK:
                updateStore.setIsPreCheckOnlyFlowState(true);
                updateStore.setIsRestoreFlowState(false);
                updateStore.setIsDryRunFlowState(false);
                break;

            case UpgradeType.UPGRADE_TYPE_SYSTEM_RESTORE:
                updateStore.setIsPreCheckOnlyFlowState(false);
                updateStore.setIsRestoreFlowState(true);
                updateStore.setIsDryRunFlowState(false);
                break;

            case UpgradeType.UPGRADE_TYPE_SYSTEM_DRYRUN:
                updateStore.setIsDryRunFlowState(true);
                updateStore.setIsRestoreFlowState(false);
                updateStore.setIsPreCheckOnlyFlowState(false);
                break;

            default:
                updateStore.setIsPreCheckOnlyFlowState(false);
                updateStore.setIsRestoreFlowState(false);
                updateStore.setIsDryRunFlowState(false);
                break;
        }

        this.preCheckProgressConfig = undefined;

        this.updateStore
            .triggerPreChecks(userSelectedOptions, false)
            .then(() => {
                this.onSelectSegUpdate.emit(this.applyToSystem);
                updateStore.setUserSelectedOptionsState(userSelectedOptions);
                updateStore.setIsSystemUpdatePageState(this.isSystemUpdate);
                updateStore.onPreChecksTrigger();
                this.currentState = UpgradeFsmState.UPGRADE_PRE_CHECK_IN_PROGRESS;
                this.wizard.forceNext();
            })
            .catch((error: IHttpResponse<IUpgradeResponse | { error?: string; status?: string }>) => {
                const errorData = error?.data as { status?: string; error?: string } | undefined;
                const errorMessage = errorData?.error ||
                    errorData?.status ||
                    this.l10nService.getMessage(l10nKeys.upgradeOperationError);

                this.error = errorMessage;
            }).finally(() => this.busy = false);
    }

    /**
     * Handle wizard close.
     */
    public handleClose(): void {
        this.error = null;
        this.busy = false;
        this.suppressWarning = false;
        this.wizard.reset();
        this.onClose.emit();
    }

    /**
     * On Seg Image selection on SEG update.
     */
    public onSegImageSelection(selectedImagesData: IImageSelectionData): void {
        const {
            selectedImagesToUpgrade,
            isSelectedImagesValidForUpgrade,
        } = selectedImagesData;

        if (isSelectedImagesValidForUpgrade && selectedImagesToUpgrade.length) {
            this.updateStore.setSelectedSegUpgradeImagesState(selectedImagesToUpgrade);
            this.upgradeConfig = UpdateService.getSegUpgradeConfig(
                this.segUpgradeNodeList,
                selectedImagesToUpgrade,
            );
        } else {
            this.updateStore.setSelectedSegUpgradeImagesState([]);
            this.upgradeConfig = {};
        }
    }

    /**
     * Return selected SEG IDs.
     */
    public get selectedSegIds(): string[] {
        if (this.segUpgradeNodeList?.length && (
            this.isType(UpgradeType.UPGRADE_TYPE_SEG_PRECHECK) ||
            this.isType(UpgradeType.UPGRADE_TYPE_SEG_UPGRADE)
        )) {
            return this.segUpgradeNodeList.map(seg => {
                return seg.data?.config?.uuid;
            });
        } else if (this.preCheckProgressConfig?.selectedSegIds?.length) {
            return this.preCheckProgressConfig?.selectedSegIds;
        } else {
            return [];
        }
    }

    /**
     * Proceed to next page for upgrade operation selection after image selection is complete.
     */
    public handleSegUpdateSelection(): void {
        this.upgradeType = UpgradeType.UPGRADE_TYPE_SEG_UPGRADE;
        this.populatePreviewCheckList();
        this.wizard.next();
    }

    public trackByEnumValue(index: number, { value }: IEnumValue): IEnumValue['value'] {
        return value;
    }

    /**
     * Handler for SEG upgrade version dropdown.
     */
    public handleSelectedSegSystemUpgradeVersionChange(): void {
        if (!this.selectedSegSystemUpgradePatchImageId) {
            delete this.upgradeConfig?.sePatchImageId;

            return;
        }

        if (this.upgradeConfig) {
            this.upgradeConfig.sePatchImageId = this.selectedSegSystemUpgradePatchImageId;
        }
    }

    /**
     * Returns the index of the array element for tracking in the template.
     */
    public trackByIndex(index: number): number {
        return index;
    }

    /**
     * Determines if the "suppress warning" option should be visible.
     *
     * It should be visible if the pre-checks wizard page is not in read-only mode,
     * the current state is a warning, and the upgrade type is not a system pre-check.
     */
    public get isSuppressWarningEnabled(): boolean {
        if (this.preCheckProgressConfig) {
            return UPGRADE_TYPES_WITH_WARNING_FLOW.has(this.upgradeType) &&
                this.currentState === this.upgradeFsmState.WARNING &&
                !this.preCheckProgressConfig.readonly;
        }

        return false;
    }

    /**
     * Navigate to the System Reports page.
     */
    public handleGoToReportPage(): void {
        this.handleClose();

        const params: Record<string, string> = {
            reportId: this.systemReportId,
        };

        this.$state.go(StateNames.REPORTS_DETAILS_SYSTEM_READINESS, params);
    }

    /**
     * Called by the pre-checks wizard page when its state changes.
     *
     * @param {UpgradeFsmState} currentState The current state of the pre-checks wizard page.
     */
    public handleCurrentPreChecksState(currentState: UpgradeFsmState): void {
        this.currentState = currentState;

        /**
         * Close the wizard on successful completion  of pre checks.
         * The update/restore progress will be tracked via the update transcript dialog.
         */
        if (this.currentState === UpgradeFsmState.UPGRADE_PRE_CHECK_SUCCESS ||
            this.upgradeType === UpgradeType.UPGRADE_TYPE_SYSTEM_DRYRUN &&
            this.currentState === UpgradeFsmState.UPGRADE_PRE_CHECK_WARNING
        ) {
            this.handleClose();
        }
    }

    /**
     * Called by the pre-checks wizard page when the next button is clicked.
     *
     * If the current state is a warning, then update the isUpdateTriggeredWithWarning flag.
     * If the current state is not a warning, then close the wizard.
     */
    public handlePreChecksPageButtonClick(): void {
        if (this.currentState === UpgradeFsmState.UPGRADE_PRE_CHECK_WARNING) {
            this.updateStore.setIsUpdateTriggeredWithWarningState(true);
            this.currentState = UpgradeFsmState.UPGRADE_PRE_CHECK_IN_PROGRESS;
        } else {
            this.handleClose();
        }
    }

    /**
     * Handle Apply To System checkbox change.
     */
    public handleApplyToSystemChange(): void {
        if (!this.applyToSystem) {
            delete this.upgradeConfig?.sePatchImageId;
        }
    }

    /**
     * Get check list from upgrade/rollback previews.
     */
    private populatePreviewCheckList(): void {
        this.busy = true;
        this.error = '';
        this.fetchUpgradePreviewFailed = false;

        this.updateService.getPreUpgradePrompts(this.upgradeConfig)
            .then(({ data: { checks } }: IHttpResponse<IUpgradePreviewResponse>) => {
                this.previewCheckList = checks;
                this.fetchUpgradePreviewFailed = false;
            })
            .catch((error: IHttpResponse<IUpgradePreviewResponse | { error?: string; status?: string }>) => {
                const errorData = error?.data as { status?: string; error?: string } | undefined;
                const errorMessage = errorData?.error ||
                    errorData?.status ||
                    this.l10nService.getMessage(l10nKeys.upgradePreviewError);

                this.error = errorMessage;
                this.fetchUpgradePreviewFailed = true;
            }).finally(() => this.busy = false);
    }

    /**
     * Set initial selected image IDs used for upgrade when wizard is first opened.
     */
    private setInitialImageIds(): void {
        const { systemImageId, sePatchImageId } = this.upgradeConfig ?? {};

        this.initialSystemImageId = systemImageId || '';
        this.selectedSegSystemUpgradePatchImageId = sePatchImageId || '';
    }

    /**
     * Initialize avi-dropdown options for compatible SEG system upgrade patch versions.
     */
    private initSegSystemUpgradePatchVersionOptions(): void {
        const { IMAGE_TYPE_PATCH } = imageTypeHash;
        const { IMAGE_CATEGORY_HYBRID } = imageCategoryHash;

        const requestBaseVersion =
            UpdateService.getBaseVersionForSystemUpgradeSegPatch(this.upgradeConfig);

        const compatiblePatchImageCollection = new this.ImageCollectionFactory(
            {
                objectName: 'image-raw-inventory',
                limit: 200,
                params: {
                    base_image_version: requestBaseVersion,
                    upgrade_operation: UpgradeOperation.SE_AS_PART_OF_SYSTEM_UPGRADE_OP,
                    image_type: IMAGE_TYPE_PATCH,
                    patch_type: PatchType.PATCH_SYSTEM,
                },
            },
        );

        compatiblePatchImageCollection.load()
            .then(() => {
                const systemPatchImages = compatiblePatchImageCollection.itemList.filter(
                    (image: Image) => image.category === IMAGE_CATEGORY_HYBRID &&
                        !image.isUsedBySeg(),
                );

                this.segSystemUpgradePatchVersionOptions =
                    this.getSegSystemUpgradePatchDropdownOptionsFromImages(
                        systemPatchImages as Image[],
                    );
            })
            .finally(() => {
                compatiblePatchImageCollection.destroy();
            });
    }

    /**
     * Get SE patch options used for avi-dropdown. System upgrade use only. Mark label as 'default'
     * if an SE system patch is already selected from the parent upon wizard opening.
     */
    private getSegSystemUpgradePatchDropdownOptionsFromImages(
        images: Image[],
    ): IAviDropdownOption[] {
        const { sePatchImageId: initialSePatchImageId } = this.upgradeConfig ?? {};

        return images.map(image => {
            const { id } = image;
            const isDefaultId = id === initialSePatchImageId;

            return createDropdownOption(
                id,
                `${image.version}${isDefaultId ? ' (default)' : ''}`,
            );
        });
    }

    /**
     * Generate wizard title based on upgrade/rollback type.
     */
    private getWizardTitle(): string {
        const { l10nService } = this;
        const { targetVersion } = this.upgradeConfig ?? {};

        if (this.isRestore) {
            return l10nService.getMessage(l10nKeys.restoreLabel);
        } else if (this.isRollback) {
            const { rollbackType } = this.upgradeConfig ?? {};

            switch (rollbackType) {
                case RollbackType.ROLLBACK_TYPE_IMAGE:
                    return l10nService.getMessage(l10nKeys.rollbackVersionHeader,
                        [targetVersion]);

                case RollbackType.ROLLBACK_TYPE_PATCH:
                    return l10nService.getMessage(l10nKeys.clearPatchHeader, [targetVersion]);
            }
        } else {
            return l10nService.getMessage(l10nKeys.updateHeader, [targetVersion ?? '']);
        }

        return '';
    }

    /**
     * Decide if wizard is handling upgrade, including both main version upgrade and patching.
     */
    private isUpgrade(): boolean {
        return this.isType(UpgradeType.UPGRADE_TYPE_SYSTEM_UPGRADE) ||
            this.isType(UpgradeType.UPGRADE_TYPE_SEG_UPGRADE);
    }

    /**
     * Decide whether the wizard is handling system upgrade.
     */
    private isSystemUpgrade(): boolean {
        return this.isType(UpgradeType.UPGRADE_TYPE_SYSTEM_UPGRADE);
    }

    /**
     * Convenience method to check upgradeType.
     */
    private isType(type: UpgradeType): boolean {
        return this.upgradeType === type;
    }
}
